import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { StyledToolbarButton, StyledTooltip } from '../../../../shared/styles/components/integrations.styles';

import Button from '@ingka/button';
import { Item } from '../../types/item.types';
import { SubItem } from '../../types/sub-item.types';
import { ToastService } from '../../../../services/toast.service';
import resetIcon from '@ingka/ssr-icon/paths/arrow-u-turn-backwards';
import { useParentItemsFromSelectedSubItems } from '../../state/selection.state';
import { useTableDataState } from '../../state/table-data.state';
import { useRefreshMutation } from '../../hooks/useRefreshMutation';

const hasActiveWeight = (subItem: SubItem) => subItem.active && subItem.totalWeight > 0;

const getConnectedItemsWithWeights = (baseItems: Item[], allItems: Item[]) => {
    const connectedItems = allItems.filter((item) => baseItems.some((baseItem) => item.parentItemNo === baseItem.itemNo));

    return connectedItems.filter((item) => item.subItems?.some(hasActiveWeight));
};

const getActiveWeightInfo = (items: Item[]) => {
    const totalParts = items.reduce((sum, item) => sum + (item.subItems?.filter(hasActiveWeight).length || 0), 0);

    return { totalParts, itemCount: items.length };
};

export const ResetConnectedButton = (): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedItems = useParentItemsFromSelectedSubItems(true);
    const tableData = useTableDataState((state) => state.data);
    const { mutate, isProcessing } = useRefreshMutation('reset');

    const baseItems = useMemo(() => selectedItems.filter((item) => item.isBase), [selectedItems]);

    const handleReset = useCallback(() => {
        setIsOpen(false);

        const itemsWithWeight = getConnectedItemsWithWeights(baseItems, tableData);

        if (itemsWithWeight.length > 0) {
            const { totalParts, itemCount } = getActiveWeightInfo(itemsWithWeight);
            ToastService.info(`There are ${totalParts} parts in ${itemCount} articles that have active weight information`);
        }

        mutate(baseItems);
    }, [baseItems, mutate, tableData]);

    const toolTipTitle = 'Reset connected items\n   and copy from base';

    return (
        <>
            <StyledTooltip title={toolTipTitle}>
                <span>
                    <StyledToolbarButton
                        ssrIcon={resetIcon}
                        text="Reset"
                        iconOnly={true}
                        small={true}
                        onClick={() => setIsOpen(true)}
                        disabled={baseItems.length === 0 || isProcessing}
                    />
                </span>
            </StyledTooltip>

            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Reset Connected Items</DialogTitle>
                <DialogContent>Are you sure you want to reset all connected items? This action cannot be undone.</DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsOpen(false)}>Cancel</Button>
                    <Button onClick={handleReset} color="primary">
                        Reset
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};